import React, { Component, PropTypes } from 'react';
import { observer, inject } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import moment from 'moment';
import Form, { FormBody, DateInput, FormActions, SelectInput } from '../../Form';
import * as api from '../../../actions/api';
import Spinner from '../../Spinner';
import LeafletMap from "../../LeafletMap";
import { getCenter } from "../../Map/getCenterLatLng";
import { getDisplayDate } from "../../../libs/date";
import ColorBar from './StationLocationMap2ColorBar';
import _ from 'lodash';

const colorRange = { R1: 1, R2: 2, R3: 3, R4: 4, R5: 5, R6: 6, R7: 7 }; // for dividing 7 values or range
const colorHexValue = { R1Color: "#0000FF", R2Color: "#6495ed", R3Color: "#00FFFF", R4Color: "#008000", R5Color: "#FFFF00", R6Color: "#FFA500", R7Color: "#FF0000" }

@inject('stationStore', 'rolePermissionStore')
@observer
class StationObservationList extends Component {
  static contextTypes = {
    notify: PropTypes.shape().isRequired,
    router: PropTypes.shape().isRequired,
  };

  static propTypes = {
    params: PropTypes.shape().isRequired,
    route: PropTypes.shape().isRequired,
  };

  @observable filters = {
    date_from: moment().subtract(30, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
    date_to: moment().add(1, 'days').format('YYYY-MM-DDT00:00:ss'),
  };
  @observable viewByParameter = 'Date & Time';
  @observable selectedMarker = null;
  @observable parameters = [];
  @observable station;
  @observable isStationaryStation = undefined;
  @observable isLoading = false;
  @observable locations = [];

  @computed get center() {
    return getCenter(this.locations);
  }
  @computed get markers() {
    return this.getMarkers(this.locations, this.viewByParameter);
  }
  @computed get extremeValues() {
    const { locations, parameters } = this;
    if (!locations.length || !parameters.length) return null;

    return parameters.reduce((acc, parameter) => {
      const values = locations.map(location => {
        const observation = (location.observations || []).find(observation => observation.parameter_id === parameter.id);
        return observation ? observation.value : null;
      });

      return Object.assign(acc, {
        [`parameter-${parameter.id}`]: {
          min: Math.min(...values),
          max: Math.max(...values),
        }
      });
    }, {});
  }
  @computed get minValue() {
    return _.get(this.extremeValues, [`parameter-${this.viewByParameter}`, 'min']);
  }
  @computed get maxValue() {
    return _.get(this.extremeValues, [`parameter-${this.viewByParameter}`, 'max']);
  }

  componentDidMount() {
    const { params: { id }, stationStore } = this.props;

    api.station.fetchParameters(id)
      .then(parameters => {
        this.parameters = [
          { name: "Date & Time", id: "Date & Time" },
          ...parameters
        ]
      }).catch(console.trace);

    const stationObj = stationStore.findById(id)
    if (stationObj.latitude && stationObj.longitude && stationObj.latitude !== null && stationObj.longitude !== null) {
      this.isStationaryStation = stationObj.latitude === -1 && stationObj.longitude === -1 ? false : true;
      this.station = stationObj;
    }
  }


  @action onChangeViewBy = e => this.viewByParameter = e.target.value;
  @action onChangeDateFrom = e => this.filters.date_from = e.target.value;
  @action onChangeDateTo = e => this.filters.date_to = e.target.value;

  @action showObservations = (e) => {
    e.preventDefault();

    if (!this.filters.date_from) return;
    if (!this.filters.date_to) return;

    this.isLoading = true;
    this.locations = [];

    api.station.fetchLocation(this.props.params.id, this.filters)
      .then((locations) => {
        this.locations = locations;
        this.isLoading = false;
      })
      .catch(console.log)
      .finally(() => {
        this.isLoading = false;
      });
  };



  getNormalizedValue(value) {
    let A = colorRange.R1; // 1
    let B = colorRange.R7; // 7
    return (A + ((value - this.minValue) * (B - A)) / (this.maxValue - this.minValue));
    //returns value to the range C,D of range A,B;
  }

  calcColor(normalizedValue) {
    const { R1Color, R2Color, R3Color, R4Color, R5Color, R6Color, R7Color } = colorHexValue;
    const { R1, R2, R3, R4, R5, R6, R7 } = colorRange;
    return (normalizedValue >= R1 && normalizedValue < R2) ? R1Color : (normalizedValue >= R2 && normalizedValue < R3) ? R2Color : (normalizedValue >= R3 && normalizedValue < R4) ? R3Color :
      (normalizedValue >= R4 && normalizedValue < R5) ? R4Color : (normalizedValue >= R5 && normalizedValue < R6) ? R5Color : (normalizedValue >= R6 && normalizedValue < R7) ? R6Color :
        (normalizedValue >= R7) ? R7Color :
          this.minValue === this.maxValue ? 'red' : ''
  }

  getColor(data) {
    const { viewByParameter } = this
    if (viewByParameter && viewByParameter !== "Date & Time" && !isNaN(viewByParameter)) {
      return this.calcColor(this.getNormalizedValue(data))
    }
    if (viewByParameter && (typeof viewByParameter === 'string') && viewByParameter === "Date & Time") {
      let date_fromInHour = new Date(moment(this.filters.date_from).format('ddd, DD MMMM YYYY HH:00:00')).getTime(); //in epoch time hr
      let date_toInHour = new Date(moment(this.filters.date_to).format('ddd, DD MMMM YYYY HH:00:00')).getTime();
      let currentDateInHour = new Date(moment(data.time).format('ddd, DD MMMM YYYY HH:00:00')).getTime();
      return this.calcColor(this.getNormalizedValue(currentDateInHour, date_fromInHour, date_toInHour))
    }

  }


  commonMarkerIcon(item, color) {
    return {
      id: `station-${this.props.params.id}@${item.time}`,
      station_id: `station-${this.props.params.id}@${item.time}`,
      position: [item.latitude, item.longitude],
      observations: item.observations,
      icon: `
        <svg id="${'ticket_marker-' + item.time}" viewBox="0 0 10 10" x="20" width="20" style="margin-left: -3px; margin-top: -5px;">
          <circle cx="5" cy="5" r="4" stroke="white" stroke-width="0.5px" fill="${color}" />
        </svg>
      `,
      iconHighlighted: `
        <svg id="${'ticket_marker-' + item.time}" viewBox="0 0 10 10" x="20" width="20" style="margin-left: -3px; margin-top: -5px; border-radius: 100px; box-shadow: #367fa9 0 0 15px 3px">
          <circle cx="5" cy="5" r="4" stroke="white" stroke-width="0.5px" fill="${color}" />
        </svg>
      `
    }
  }

  getMarkers(locations, viewByParameter) {
    return locations.map(item => {
      if (viewByParameter && !isNaN(viewByParameter)) {
        if (!item.observations) return this.commonMarkerIcon(item, this.getColor(1));
        let parameterValue = (item.observations || []).find(i => +i.parameter_id === +viewByParameter).value;
        // console.log('parameterValue : ', parameterValue)
        let colorByParameter = this.getColor(parameterValue);
        return this.commonMarkerIcon(item, colorByParameter);
      }
      if (viewByParameter && (typeof viewByParameter === 'string') && viewByParameter === "Date & Time") {
        let colorByDateAndTime = this.getColor(item);
        return this.commonMarkerIcon(item, colorByDateAndTime);
      }
      else {
        return this.commonMarkerIcon(item, "blue")
      }
    });
  }

  renderStationaryStation() {
    return (
      <div>
        {
          this.station ?
            <LeafletMap id={`MapMarker-${this.props.params.id}-location-map`}

              markers={[{
                position: [this.station.latitude, this.station.longitude],
                id: `mapMarker-${this.station.id}@${this.station.name}`,
                popupBox: this.station.name
              }]}
              center={{ lat: this.station.latitude, lng: this.station.longitude }}
              isStationaryStation={this.isStationaryStation}
            />
            : <Spinner />}
      </div>
    )
  }

  getPopupBox = marker => {
    if (!marker) return null;
    return (
      <div className='map-info-box'>
        <strong style={{ fontSize: '15px', fontWeight: 'bold' }}>{getDisplayDate(marker.time)}</strong><br />
        <span className="fa fa-close" style={{ position: 'absolute', right: 2, top: 2 }} onClick={() => this.selectedMarker = null} />
        <br />
        {marker.observations && <div style={{ maxHeight: '220px', overflowY: 'scroll', margin: '4px' }}>
          {marker.observations.map((parameter, i) => (
            <div key={`location-${marker.time}-${parameter.name}-${i}`} className="popup-table-item">
              <strong>{parameter.name}</strong>
              <div>{parameter.value}</div>
            </div>
          ))}
        </div>}
      </div>
    );
  }

  renderMovingStation() {
    const { parameters, viewByParameter } = this;

    return (
      <div>
        <Form direction='row'>
          <FormBody>
            <DateInput
              label={'Date From'}
              value={this.filters.date_from}
              onChange={this.onChangeDateFrom}
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
              }}
            />
            <DateInput
              label={'Date To'}
              value={this.filters.date_to}
              onChange={this.onChangeDateTo}
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
              }}
            />
            <SelectInput label='View By' value={viewByParameter} onChange={this.onChangeViewBy} options={parameters} fields={{ label: 'name', value: 'id' }} />
          </FormBody>
          <FormActions>
            <button onClick={this.showObservations}>View</button>
          </FormActions>
        </Form><br />
        {this.isLoading ? <Spinner /> : !!this.markers.length && <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-around" }}>
          {!this.isLoading && !!this.markers.length &&
            <LeafletMap id={`station-${this.props.params.id}-location-map`} markers={this.markers} center={this.center} selectedMarker={this.selectedMarker && this.selectedMarker.id} onMarkerOver={marker => this.selectedMarker = marker} maxZoom={35} >
              {this.getPopupBox(this.selectedMarker)}
            </LeafletMap>}
          <ColorBar
            parameter={parameters && viewByParameter && !isNaN(viewByParameter) ? viewByParameter :
              typeof (viewByParameter === 'string') && viewByParameter === "Date & Time" ? "Date & Time" : ''}
            range={colorRange}
            render={!this.isLoading && !!this.markers.length}
            filters={this.filters}
            minValue={this.minValue}
            maxValue={this.maxValue}
          />

        </div>
        }
      </div>
    )
  }

  render() {
    return (
      <div>
        {
          this.isStationaryStation !== undefined && (typeof this.isStationaryStation === "boolean") ?
            this.isStationaryStation === true ? this.renderStationaryStation() : this.renderMovingStation()
            : ''
        }
      </div >
    );
  }
}

export default StationObservationList;


// WEBPACK FOOTER //
// ./src/components/Station/Location/StationLocationMap2.js